import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { AppService } from '@src/suite/app.service';
import { CastSearchService } from '@src/suite/cast/services/cast-search-service';
import { CastService } from '@src/suite/cast/services/cast-service';
import { ModalResponseCodes, ModalService } from '@src/suite/common/components/modal/modal.service';
import { DateTime } from '@src/suite/common/data-models/complex-types/date-time';
import { PublicationViewStateRecord } from '@src/suite/common/data-models/records/publication-viewstate-record';
import { RecordFactory } from '@src/suite/common/data-models/records/record-factory';
import { SearchViewStateRecord } from '@src/suite/common/data-models/records/search-viewstate-record';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { LayoutState } from '@src/suite/common/services/layout-state';
import { PublicationChannelsService } from '@src/suite/common/services/publication-channels-service';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { ListActions } from '@src/suite/common/store/list/list.actions';
import { selectListGuids } from '@src/suite/common/store/list/list.selectors';
import { ApplicationActions } from '@src/suite/common/store/navigation/actions/application.actions';
import { ViewStateActions } from '@src/suite/common/store/navigation/actions/viewstate.actions';
import { selectCurrentApplication } from '@src/suite/common/store/navigation/selectors/navigation.selectors';
import { selectCurrentLayout } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { AssignmentRecordActions, PublicationRecordActions, StoriesRecordActions, TopicRecordActions } from '@src/suite/common/store/records/records.actions';
import { selectAssignmentRecordsMap, selectPublicationChannelsArray, selectPublicationListsMap, selectPublications, selectTopicRecordsMap } from '@src/suite/common/store/records/records.selectors';
import { PlaybookSearchService } from '@src/suite/playbook/services/playbook-search-service';
import { catchError, debounceTime, exhaustMap, filter, forkJoin, from, map, mergeMap, Observable, of, switchMap, take } from 'rxjs';

import { CastActions, CastApiActions } from './cast.actions';
import {
    selectCurrentCastViewState,
    selectCurrentPublicationChannel,
    selectCurrentPublicationList,
    selectCurrentPublicationListGuid,
    selectCurrentPublicationListSections,
    selectCurrentPublicationListSectionsWithLinkedRecordGuids,
    selectDragRecordGuid,
    selectHasMoreThanOneSection,
    selectSelectedSavedViewState,
    selectSideRecords,
} from './cast.selectors';
import { CANDIDATE_ID_PREFIX, PUBLICATION_ID_PREFIX, SECTION_ID_PREFIX, SECTIONS_SEQUENCE_FIELD } from './cast.utils';

@Injectable()
export class CastStateEffects {
    private actions$ = inject(Actions);
    private store = inject(Store);
    private modalService = inject(ModalService);
    private viewStateService = inject(LayoutState);
    private castSearchService = inject(CastSearchService);
    private castService = inject(CastService);
    private publicationChannelsService = inject(PublicationChannelsService);
    private playbookSearchService = inject(PlaybookSearchService);
    private appService = inject(AppService);

    private languageSettings = this.store.selectSignal(selectI18N)();

    fallbackActions = [CastActions.setDragRecordGuid({ guid: null }), CastActions.setProcessing({ processing: false })];

    onCurrentViewStateSuccess = createEffect(() => {
        return this.actions$.pipe(
            ofType(ViewStateActions.setCurrentViewStateSuccess, PublicationRecordActions.updatePublicationChannels),
            concatLatestFrom(() => [this.store.select(selectCurrentCastViewState)]),
            filter(([, currentViewState]) => currentViewState instanceof PublicationViewStateRecord && currentViewState.publicationChannel != null),
            switchMap(([_, currentViewState]) => {
                return [
                    CastActions.setLoadingList({ listLoading: true }),
                    CastApiActions.loadPublicationList({ vs: currentViewState as PublicationViewStateRecord, setMaxPublicationRevisionNumber: true }),
                ].filter(Boolean);
            })
        );
    });

    onLoadPublicationLists = createEffect(() => {
        return this.actions$.pipe(
            ofType(CastApiActions.loadPublicationList),
            filter(action => action.vs instanceof PublicationViewStateRecord && action?.vs?.publicationChannel != null),
            switchMap(action => {
                return from(this.castSearchService.getSearchResultsV2(action.vs, action.loadSpecificRevision)).pipe(
                    filter(res => res.status),
                    map(res => res.data),
                    mergeMap(castResult => {
                        const { publicationList, assignments, topics, listGuids } = castResult;

                        let newViewState: PublicationViewStateRecord = null;
                        if (action.setMaxPublicationRevisionNumber) {
                            newViewState = RecordFactory.createViewStateForType(action.vs.viewStateType) as PublicationViewStateRecord;
                            action.vs.copyDataTo(newViewState, true);
                            if (publicationList && publicationList.PSTPublicationListRevisionNumber) {
                                newViewState.publicationRevisionNumber = newViewState.maxPublicationRevisionNumber = parseInt(publicationList.PSTPublicationListRevisionNumber);
                            } else {
                                newViewState.publicationRevisionNumber = newViewState.maxPublicationRevisionNumber = 0;
                            }
                        }
                        if (publicationList) {
                            return [
                                PublicationRecordActions.updatePublicationList({ records: [publicationList] }),
                                PublicationRecordActions.updatePublications({ records: publicationList.children || [] }),
                                AssignmentRecordActions.updateAssignments({ records: [...(assignments?.values() || [])] }),
                                TopicRecordActions.updateTopics({ records: [...(topics?.values() || [])] }),
                                ListActions.setListGuids({ listGuids: listGuids }),
                                CastActions.setCurrentPublicationListGuid({ guid: publicationList.Guid }),
                                action.setMaxPublicationRevisionNumber ? ViewStateActions.updateViewStateMaxRevisionNumber({ vs: newViewState }) : null,
                                ...this.fallbackActions,
                            ].filter(Boolean);
                        } else {
                            return [CastActions.setCurrentPublicationListGuid({ guid: null }), ...this.fallbackActions];
                        }
                    })
                );
            }),
            catchError(() => [CastActions.setCurrentPublicationListGuid({ guid: null }), ...this.fallbackActions])
        );
    });

    onPublicationRevisionNumberUpdated = createEffect(() => {
        return this.actions$.pipe(
            ofType(ViewStateActions.updateViewStateRevisionNumber),
            concatLatestFrom(() => [this.store.select(selectCurrentPublicationList)]),
            filter(([, currentPublicationList]) => currentPublicationList != null),
            switchMap(() => {
                return of(
                    CastApiActions.loadPublicationList({
                        vs: this.store.selectSignal(selectCurrentCastViewState)() as PublicationViewStateRecord,
                        setMaxPublicationRevisionNumber: false,
                        loadSpecificRevision: true,
                    })
                );
            })
        );
    });

    loadPublicationChannels$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CastApiActions.loadPublicationsChannels),
            switchMap(() => {
                return from(this.publicationChannelsService.getPublicationChannelRecords()).pipe(
                    filter(res => res.status && res.data?.length > 0),
                    exhaustMap(res => {
                        return [PublicationRecordActions.updatePublicationChannels({ records: res.data })];
                    })
                );
            })
        );
    });

    loadSavedViewStates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CastApiActions.loadViewStates),
            switchMap(() => {
                return from(this.viewStateService.getViewStateRecords('playbook', null, 'group')).pipe(
                    map(({ data }) => data?.views),
                    exhaustMap(views => {
                        const actions = [];
                        actions.push(CastActions.setViewStates({ viewStates: views }));
                        const vs = this.store.selectSignal(selectCurrentCastViewState)();
                        const preselectedVS = vs.viewStateGUID && views.find(v => v.guid === vs.viewStateGUID) ? vs.viewStateGUID : views?.[0]?.guid;
                        if (preselectedVS) {
                            actions.push(CastActions.setCurrentViewState({ guid: preselectedVS }));
                        }
                        if (actions.length > 0) {
                            return actions;
                        }
                        return of(ApplicationActions.noOpAction());
                    }),
                    catchError(() => of(ApplicationActions.noOpAction()))
                );
            })
        );
    });

    onSearchTermChanged$ = createEffect(() => {
        return this.actions$.pipe(
            debounceTime(300),
            ofType(CastApiActions.searchRecords),
            switchMap(({ searchTerm }) => {
                if (!searchTerm) {
                    return [CastActions.clearSearchTerm(), CastActions.stopSideRecordLoading()];
                }
                const vs: SearchViewStateRecord = new SearchViewStateRecord();
                vs.viewStateSearchQuery = searchTerm;
                vs.recordTypes = [ObjectTypes.TOPIC, ObjectTypes.ASSIGNMENT];
                return this.loadRecordsByVS(vs);
            })
        );
    });

    loadFilterRecords$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CastActions.setCurrentViewState, CastApiActions.reloadRecordsForSelectedViewState),
            switchMap(() => {
                const viewState = this.store.selectSignal(selectSelectedSavedViewState)();
                if (viewState?.guid) {
                    return this.loadRecordsByVS(viewState as SearchViewStateRecord);
                }
                return of(CastActions.stopSideRecordLoading());
            })
        );
    });

    onAddUpdatePublicationList$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CastApiActions.addUpdatePublicationList),
            switchMap(({ droppedGuid }) => {
                const vs = this.store.selectSignal(selectCurrentCastViewState)() as PublicationViewStateRecord;

                const sections = vs.publicationChannel?.PSTPublicationChannelSections?.split('|')
                    .map((item: string) => item.trim())
                    .filter(Boolean);
                const publicationList = this.store.selectSignal(selectCurrentPublicationList)();

                return this.getPublicationList(publicationList?.Guid).pipe(
                    mergeMap(list => {
                        let isCandidate: boolean = false;
                        let isPublication: boolean = false;
                        let dropInSection: boolean = false;
                        let sourceSection: string = '';
                        let targetSection: string = '';
                        let sourceIndex: number = -1;
                        let targetIndex: number = -1;
                        let draggedGuid = this.store.selectSignal(selectDragRecordGuid)();
                        if (draggedGuid?.startsWith(CANDIDATE_ID_PREFIX)) {
                            isCandidate = true;
                            draggedGuid = draggedGuid.replace(CANDIDATE_ID_PREFIX, '');
                        } else if (draggedGuid?.startsWith(PUBLICATION_ID_PREFIX)) {
                            isPublication = true;
                            draggedGuid = draggedGuid.replace(PUBLICATION_ID_PREFIX, '');
                        }
                        if (droppedGuid?.startsWith(SECTION_ID_PREFIX)) {
                            droppedGuid = droppedGuid.replace(SECTION_ID_PREFIX, '');
                            targetSection = droppedGuid;
                            dropInSection = true;
                        } else if (droppedGuid?.startsWith(PUBLICATION_ID_PREFIX)) {
                            droppedGuid = droppedGuid.replace(PUBLICATION_ID_PREFIX, '');
                        }

                        let publicationSequenceCollection: {
                            [section: string]: Array<string>;
                        } = {};
                        try {
                            publicationSequenceCollection = JSON.parse(list.PublicationSequenceCollection);
                        } catch (e) {
                            for (const section of sections) {
                                publicationSequenceCollection[section] = [];
                            }
                        }
                        for (const section in publicationSequenceCollection) {
                            if (section !== SECTIONS_SEQUENCE_FIELD) {
                                if (!Array.isArray(publicationSequenceCollection[section])) {
                                    continue;
                                }
                                for (let i = 0; i < publicationSequenceCollection[section].length; i++) {
                                    if (publicationSequenceCollection[section][i] === draggedGuid) {
                                        sourceSection = section;
                                        sourceIndex = i;
                                    }
                                    if (!dropInSection && publicationSequenceCollection[section][i] === droppedGuid) {
                                        targetSection = section;
                                        if (sourceIndex < 0) {
                                            targetIndex = i + 1;
                                        } else {
                                            targetIndex = i;
                                        }
                                    }
                                }
                            }
                        }

                        /*
                        // TODO: need to remove this one, left for reference
                        if (isCandidate) {
                            targetIndex = targetIndex + 1;
                        }
                        if (sourceIndex > targetIndex) {
                            targetIndex = targetIndex + 1;
                        }
                        */
                        if (targetIndex < 0) {
                            targetIndex = 0;
                        }
                        if (targetIndex > publicationSequenceCollection[targetSection].length) {
                            targetIndex = publicationSequenceCollection[targetSection].length;
                        }
                        if (isCandidate) {
                            const availableRecords = this.store.selectSignal(selectSideRecords)() || [];
                            const candidate = availableRecords.find(r => r.Guid === draggedGuid);
                            if (!candidate) {
                                return this.fallbackActions;
                            }
                            const alreadyLinkedBySection = this.store.selectSignal(selectCurrentPublicationListSectionsWithLinkedRecordGuids)();
                            // if the same candidate is already added in the target section we need to skip
                            if (alreadyLinkedBySection?.[targetSection]?.includes(candidate.Guid)) {
                                return this.fallbackActions;
                            }

                            return this.handleRestrictedChannelTypes([candidate.Guid]).pipe(
                                exhaustMap(result => {
                                    if (result) {
                                        const newPublication = {
                                            Title: candidate.Title,
                                            Slug: candidate.Title,
                                            ModTime: new DateTime(new Date()),
                                            Type: ObjectTypes.PUBLICATION,
                                            linkedRecords: [candidate],
                                        } as StorytellingPublicationRecord & { linkedRecord: StorytellingRecord };

                                        return from(this.castService.createPublications([newPublication], targetSection, targetIndex, [], list)).pipe(
                                            filter(createdPublicationsResp => createdPublicationsResp.status),
                                            exhaustMap(() => {
                                                return [CastApiActions.loadPublicationList({ vs }), CastActions.setDragRecordGuid({ guid: null })];
                                            })
                                        );
                                    } else {
                                        return this.fallbackActions;
                                    }
                                })
                            );
                        } else if (isPublication) {
                            // We are droping an existing publication from that list, to the same or another section
                            if (targetIndex < 0) targetIndex = publicationSequenceCollection[targetSection].length;
                            if (sourceSection === targetSection && sourceIndex === targetIndex) {
                                // Nothing's changed
                                return this.fallbackActions;
                            }
                            const newSections = { ...publicationSequenceCollection };
                            // Dragging cross-section. Need to check if we aren't trying to put something that already exists
                            if (sourceSection !== targetSection && newSections?.[targetSection]?.includes(draggedGuid)) {
                                return this.fallbackActions;
                            }

                            const alreadyLinkedBySection = this.store.selectSignal(selectCurrentPublicationListSectionsWithLinkedRecordGuids)();
                            const publication = this.store.selectSignal(selectPublications)()?.get(draggedGuid);

                            // if the same candidate is already added in the target section we need to skip
                            if (sourceSection !== targetSection && alreadyLinkedBySection?.[targetSection]?.includes(publication?.linkedRecords?.[0]?.Guid)) {
                                return this.fallbackActions;
                            }

                            newSections[targetSection].splice(targetIndex, 0, newSections[sourceSection].splice(sourceIndex, 1)[0]);

                            return this.updatePublicationRecords({ sourceSection, targetSection, sourceIndex, targetIndex }, newSections, publicationList).pipe(
                                exhaustMap(() => [CastApiActions.loadPublicationList({ vs }), CastActions.setDragRecordGuid({ guid: null })])
                            );
                        }
                        return this.fallbackActions;
                    })
                );
            }),
            catchError(() => this.fallbackActions)
        );
    });

    onAddPublicationsToSection = createEffect(() => {
        return this.actions$.pipe(
            ofType(CastApiActions.addPublicationsToPublicationListSection),
            switchMap(({ section, recordsToAdd }) => {
                return this.getPublicationList(this.store.selectSignal(selectCurrentPublicationListGuid)()).pipe(
                    switchMap(publicationList => {
                        const availableRecords = this.store.selectSignal(selectSideRecords)() || [];
                        const alreadyLinkedBySection = this.store.selectSignal(selectCurrentPublicationListSectionsWithLinkedRecordGuids)();
                        const newPublications = recordsToAdd
                            .map(r => {
                                const candidate = availableRecords.find(ar => ar.Guid === r);
                                if (!candidate) {
                                    return null;
                                }
                                if (alreadyLinkedBySection?.[section]?.includes(candidate.Guid)) {
                                    return null;
                                }
                                return {
                                    Title: candidate.Title,
                                    Slug: candidate.Title,
                                    ModTime: new DateTime(new Date()),
                                    Type: ObjectTypes.PUBLICATION,
                                    linkedRecords: [candidate],
                                } as StorytellingPublicationRecord & { linkedRecord: StorytellingRecord };
                            })
                            .filter(p => !!p) as (StorytellingPublicationRecord & { linkedRecord: StorytellingRecord })[];

                        if (!newPublications.length) {
                            return this.fallbackActions;
                        }

                        return this.handleRestrictedChannelTypes(newPublications?.map(p => p?.linkedRecords?.[0]?.Guid)).pipe(
                            exhaustMap(result => {
                                if (result) {
                                    const newSections = { ...this.store.selectSignal(selectCurrentPublicationListSections)() };
                                    const targetIndex = newSections[section].length;

                                    newSections[section].push(...newPublications.map(p => p.Guid));
                                    return from(this.castService.createPublications(newPublications, section, targetIndex, [], publicationList)).pipe(
                                        filter(createdPublicationsResp => createdPublicationsResp.status),
                                        exhaustMap(() => {
                                            const vs = this.store.selectSignal(selectCurrentCastViewState)() as PublicationViewStateRecord;
                                            return [CastApiActions.loadPublicationList({ vs }), CastActions.setDragRecordGuid({ guid: null })];
                                        })
                                    );
                                } else {
                                    return this.fallbackActions;
                                }
                            })
                        );
                    })
                );
            }),
            catchError(() => this.fallbackActions)
        );
    });

    onDeletePublicationSection = createEffect(() => {
        return this.actions$.pipe(
            ofType(CastApiActions.deleteSection),
            switchMap(({ section }) => {
                return this.getPublicationList(this.store.selectSignal(selectCurrentPublicationListGuid)()).pipe(
                    switchMap(publicationList => {
                        const publicationSections = this.store.selectSignal(selectCurrentPublicationListSections)();
                        const hasMoreThanOneSection = this.store.selectSignal(selectHasMoreThanOneSection)();
                        if (!hasMoreThanOneSection) return this.fallbackActions;
                        return this.modalService.openModal(this.languageSettings.PSTWebConfirmDeleteSectionLabel, this.languageSettings.PSTWebConfirmDeleteSectionMessage, 'Yes', 'No').result.pipe(
                            take(1),
                            switchMap(res => {
                                if (res !== ModalResponseCodes.OK) return this.fallbackActions;
                                return this.deletePublications(publicationSections[section], publicationSections, publicationList).pipe(
                                    switchMap(success => {
                                        if (!success) return this.fallbackActions;
                                        const newSections = { ...publicationSections };
                                        delete newSections[section];
                                        if (newSections[SECTIONS_SEQUENCE_FIELD]) {
                                            newSections[SECTIONS_SEQUENCE_FIELD] = newSections[SECTIONS_SEQUENCE_FIELD].filter(s => s !== section);
                                        } else {
                                            newSections[SECTIONS_SEQUENCE_FIELD] = Object.keys(newSections) || [];
                                        }
                                        return this.updatePublicationListSequence(newSections[SECTIONS_SEQUENCE_FIELD] || [], newSections, publicationList).pipe(
                                            exhaustMap(() => [CastApiActions.loadPublicationList({ vs: this.store.selectSignal(selectCurrentCastViewState)() as PublicationViewStateRecord })])
                                        );
                                    })
                                );
                            })
                        );
                    })
                );
            }),
            catchError(() => this.fallbackActions)
        );
    });

    onDeletePublications = createEffect(() => {
        return this.actions$.pipe(
            ofType(CastApiActions.deletePublications),
            switchMap(({ publicationGuidsToDelete }) => {
                return this.getPublicationList(this.store.selectSignal(selectCurrentPublicationListGuid)()).pipe(
                    switchMap(publicationList => {
                        const publicationsMap = this.store.selectSignal(selectPublications)();
                        const title =
                            publicationGuidsToDelete.length > 1
                                ? this.languageSettings.PSTWebConfirmDeletePublcationItems
                                : publicationGuidsToDelete[0]
                                  ? publicationsMap.get(publicationGuidsToDelete[0])?.Title
                                  : '';
                        const modalContent = `${this.languageSettings.PSTWebConfirmDeletePart1} ${title} ${this.languageSettings.PSTWebConfirmDeletePublcationItem}`;
                        return this.modalService
                            .openModal(this.languageSettings.PSTWebConfirmDeleteLabel, modalContent, this.languageSettings.PSTWebYesOption, this.languageSettings.PSTWebNoOption)
                            .result.pipe(
                                switchMap(res => {
                                    if (res !== ModalResponseCodes.OK) return this.fallbackActions;
                                    const publicationSections = this.store.selectSignal(selectCurrentPublicationListSections)();
                                    return this.deletePublications(publicationGuidsToDelete, publicationSections, publicationList).pipe(
                                        exhaustMap(() => [CastApiActions.loadPublicationList({ vs: this.store.selectSignal(selectCurrentCastViewState)() as PublicationViewStateRecord })])
                                    );
                                })
                            );
                    })
                );
            }),
            catchError(() => this.fallbackActions)
        );
    });

    onRenamePublicationListSection = createEffect(() => {
        return this.actions$.pipe(
            ofType(CastApiActions.renamePublicationListSection),
            switchMap(({ oldSection, newSection }) => {
                return this.getPublicationList(this.store.selectSignal(selectCurrentPublicationListGuid)()).pipe(
                    switchMap(publicationList => {
                        const publicationSections = this.store.selectSignal(selectCurrentPublicationListSections)();
                        const newSections = { ...publicationSections };
                        newSections[newSection] = newSections[oldSection];
                        delete newSections[oldSection];
                        if (newSections[SECTIONS_SEQUENCE_FIELD]) {
                            newSections[SECTIONS_SEQUENCE_FIELD] = newSections[SECTIONS_SEQUENCE_FIELD].map(section => (section === oldSection ? newSection : section));
                        } else {
                            newSections[SECTIONS_SEQUENCE_FIELD] = Object.keys(newSections) || [];
                        }
                        return this.updatePublicationListSequence(newSections[SECTIONS_SEQUENCE_FIELD] || [], newSections, publicationList).pipe(
                            exhaustMap(() => [CastApiActions.loadPublicationList({ vs: this.store.selectSignal(selectCurrentCastViewState)() as PublicationViewStateRecord })])
                        );
                    })
                );
            }),
            catchError(() => this.fallbackActions)
        );
    });

    onAddPublicationListSection = createEffect(() => {
        return this.actions$.pipe(
            ofType(CastApiActions.addPublicationListSection),
            switchMap(({ section, idx }) => {
                return this.getPublicationList(this.store.selectSignal(selectCurrentPublicationListGuid)()).pipe(
                    switchMap(publicationList => {
                        const publicationSections = this.store.selectSignal(selectCurrentPublicationListSections)();
                        const newSections = { ...publicationSections };
                        newSections[section] = [];
                        if (newSections[SECTIONS_SEQUENCE_FIELD]) {
                            newSections[SECTIONS_SEQUENCE_FIELD].splice(idx, 0, section);
                        } else {
                            newSections[SECTIONS_SEQUENCE_FIELD] = Object.keys(newSections) || [];
                        }
                        return this.updatePublicationListSequence(newSections[SECTIONS_SEQUENCE_FIELD] || [], newSections, publicationList).pipe(
                            exhaustMap(() => [CastApiActions.loadPublicationList({ vs: this.store.selectSignal(selectCurrentCastViewState)() as PublicationViewStateRecord })])
                        );
                    })
                );
            }),
            catchError(() => this.fallbackActions)
        );
    });

    onClearPublicationList = createEffect(() => {
        return this.actions$.pipe(
            ofType(CastApiActions.clearCurrentPublicationList),
            exhaustMap(() => {
                return this.getPublicationList(this.store.selectSignal(selectCurrentPublicationListGuid)()).pipe(
                    switchMap(publicationList => {
                        const allPublications = this.store.selectSignal(selectListGuids)();
                        const publicationSections = this.store.selectSignal(selectCurrentPublicationListSections)();
                        return this.deletePublications(allPublications, publicationSections, publicationList).pipe(
                            exhaustMap(() => [CastApiActions.loadPublicationList({ vs: this.store.selectSignal(selectCurrentCastViewState)() as PublicationViewStateRecord })])
                        );
                    })
                );
            }),
            catchError(() => this.fallbackActions)
        );
    });

    onPublishCurrentPublicationList = createEffect(() => {
        return this.actions$.pipe(
            ofType(CastApiActions.publishCurrentPublicationList),
            switchMap(() => {
                return this.getPublicationList(this.store.selectSignal(selectCurrentPublicationListGuid)()).pipe(
                    switchMap(publicationList => {
                        const sections = this.store.selectSignal(selectCurrentPublicationListSections)();
                        const publications = this.store.selectSignal(selectPublications)();
                        const sectionsWithPublications = {};

                        Object.keys(sections).forEach(key => {
                            if (!key || !sections[key] || !sections[key].length || key === SECTIONS_SEQUENCE_FIELD) return;
                            sectionsWithPublications[key] = sections[key].map(guid => publications.get(guid));
                        });

                        return from(
                            this.castService.publishPublicationList(publicationList, this.store.selectSignal(selectCurrentPublicationChannel)().PSTPublicationChannelActionID, sectionsWithPublications)
                        ).pipe(exhaustMap(() => [CastApiActions.loadPublicationList({ vs: this.store.selectSignal(selectCurrentCastViewState)() as PublicationViewStateRecord })]));
                    })
                );
            }),
            catchError(() => this.fallbackActions)
        );
    });

    onNewRevisionForCurrentPublicationList = createEffect(() => {
        return this.actions$.pipe(
            ofType(CastApiActions.createNewRevisionForCurrentPublicationList),
            switchMap(() => {
                return this.getPublicationList(this.store.selectSignal(selectCurrentPublicationListGuid)()).pipe(
                    switchMap(publicationList => {
                        return from(this.castService.newRevision(publicationList)).pipe(
                            map(res => res.data),
                            map(data => {
                                if (data) {
                                    const vs = this.store.selectSignal(selectCurrentCastViewState)() as PublicationViewStateRecord;
                                    vs.publicationRevisionNumber = parseInt(data.PSTPublicationListRevisionNumber);
                                    vs.maxPublicationRevisionNumber = parseInt(data.PSTPublicationListRevisionNumber);
                                    return vs;
                                }
                                return null;
                            }),
                            exhaustMap(vs => {
                                if (!vs) return this.fallbackActions;
                                return [
                                    ViewStateActions.setCurrentViewStateSuccess({ vs, layout: this.store.selectSignal(selectCurrentLayout)() }),
                                    CastApiActions.loadPublicationList({ vs, loadSpecificRevision: false, setMaxPublicationRevisionNumber: true }),
                                ];
                            })
                        );
                    })
                );
            }),
            catchError(() => this.fallbackActions)
        );
    });

    updateViewState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ViewStateActions.viewStateChanged),
            mergeMap(() => {
                const application = this.store.selectSignal(selectCurrentApplication)();
                const currentViewState = this.store.selectSignal(selectCurrentCastViewState)();
                if (application === 'cast' && currentViewState && currentViewState instanceof PublicationViewStateRecord && currentViewState.viewStateGUID) {
                    return of(CastActions.setCurrentViewState({ guid: currentViewState.viewStateGUID }));
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    handleRestrictedChannelTypes(publicationGuids: string[]): Observable<boolean> {
        const restrictedPublicationChannelTypes = this.appService.restrictedPublicationChannelTypes;
        const channel = this.store.selectSignal(selectCurrentPublicationChannel)();

        if (restrictedPublicationChannelTypes.includes(channel.PSTPublicationChannelType)) {
            const vs = this.store.selectSignal(selectCurrentCastViewState)() as PublicationViewStateRecord;
            const channelGuids = [...(this.store.selectSignal(selectPublicationChannelsArray)() || [])]
                .filter(ch => ch.PSTPublicationChannelType === channel.PSTPublicationChannelType)
                .map(ch => ch.Guid);

            if (!channelGuids?.length) {
                return of(false);
            }

            return from(this.castService.loadPublicationLists(vs, channelGuids)).pipe(
                exhaustMap(data => {
                    if (data.status) {
                        const publicationListsOfRestrictedChannelTypes = data.data?.publicationListsOfRestrictedChannelTypes;
                        const existsInAnotherLists = publicationListsOfRestrictedChannelTypes.filter(
                            list => !list.PSTPublicationListPublishedTime && list.children.some(child => publicationGuids.includes((<StorytellingPublicationRecord>child)?.Relationships?.getGuid(0)))
                        );
                        const publicationList = this.store.selectSignal(selectCurrentPublicationList)() as StorytellingPublicationListRecord;
                        const existsInTheCurrentList = existsInAnotherLists.some(list => !!(publicationList && publicationList.Guid === list.Guid));

                        if (existsInAnotherLists.length > 0 && !existsInTheCurrentList) {
                            return this.restrictedChannelTypesModal(existsInAnotherLists, publicationGuids);
                        }
                    }
                    return of(true);
                })
            );
        }
        return of(true);
    }

    restrictedChannelTypesModal(restrictedChannelPublicationLists: StorytellingPublicationListRecord[], publicationGuids: string[]): Observable<boolean> {
        const listNames = restrictedChannelPublicationLists.map(list => list.Title).join(', ');
        // add case for multiple publications
        const topics = this.store.selectSignal(selectTopicRecordsMap)();
        const assignments = this.store.selectSignal(selectAssignmentRecordsMap)();
        const publicationName = publicationGuids?.length === 1 ? topics.get(publicationGuids[0])?.Title || assignments.get(publicationGuids[0])?.Title : '';

        const dialogMessage =
            publicationGuids?.length > 1
                ? this.languageSettings.PSTWebConfirmAddPublicationsToRestrictedChannelMessage
                : this.languageSettings.PSTWebConfirmAddPublicationToRestrictedChannelMessage.replace('{publicationLists}', listNames).replace('{publicationName}', publicationName);
        return this.modalService.openModal(this.languageSettings.PSTWebConfirmAddPublicationToRestrictedChannelLabel, dialogMessage, 'Yes', 'No').result.pipe(
            take(1),
            exhaustMap(result => {
                if (result === ModalResponseCodes.OK) {
                    return this.removePublicationsFromRestrictedChannelLists(publicationGuids, restrictedChannelPublicationLists);
                }
                return of(false);
            }),
            catchError(() => of(false))
        );
    }

    removePublicationsFromRestrictedChannelLists(publicationsToRemove: string[], restrictedChannelPublicationLists: Array<StorytellingPublicationListRecord>): Observable<boolean> {
        //         // remove from the prior list(s) and return true to add it in the new list
        const deletePublicationsArgsList = [];
        for (const list of restrictedChannelPublicationLists) {
            const channelSections: StorytellingPublicationListSections = {};
            const publications = <Array<StorytellingPublicationRecord>>(
                list.children.filter(child =>
                    (<StorytellingPublicationRecord>child)?.Relationships ? publicationsToRemove.includes((<StorytellingPublicationRecord>child).Relationships?.getGuid(0)) : false
                )
            );
            let publicationSequenceCollection: any = {};
            if (list && list.PublicationSequenceCollection) {
                publicationSequenceCollection = JSON.parse(list.PublicationSequenceCollection);
            }

            const publicationListSections = Object.keys(publicationSequenceCollection).filter(section => section !== SECTIONS_SEQUENCE_FIELD);
            const recordSectionMap: { [publicationRecordGuid: string]: string } = {};
            for (const section of publicationListSections) {
                channelSections[section] = new Array<string>();
                if (publicationSequenceCollection[section]) {
                    if (!Array.isArray(publicationSequenceCollection[section])) {
                        publicationSequenceCollection[section] = publicationSequenceCollection[section].split(';');
                    }
                    for (const guid of publicationSequenceCollection[section]) {
                        if (guid) {
                            const publicationRecord = publications.find(p => p.Guid === guid);
                            if (publicationRecord) {
                                channelSections[section].push(publicationRecord.Guid);
                                recordSectionMap[publicationRecord.Guid] = section;
                            }
                        }
                    }
                }
            }
            for (const publicationRecord of publications) {
                const section = recordSectionMap[publicationRecord.Guid];
                const index = channelSections[section].findIndex(p => p === publicationRecord.Guid);
                const publicationArgs = {
                    publicationList: list,
                    section,
                    index,
                    channelSections,
                };
                deletePublicationsArgsList.push(publicationArgs);
            }
        }
        return from(this.castService.deletePublicationRecords(deletePublicationsArgsList)).pipe(
            map(data => data?.status),
            catchError(() => of(false))
        );
    }

    // local functions
    loadRecordsByVS(viewState: SearchViewStateRecord) {
        return from(this.playbookSearchService.getPlaybookResults(viewState)).pipe(
            map(data => data?.data),
            exhaustMap(results => {
                const actions = [];
                actions.push(CastActions.setSideRecordGuids({ guids: [...(results?.listGuids || [])] }));

                if (results?.stories) {
                    actions.push(StoriesRecordActions.updateStories({ records: [...(results.stories.values() || [])] }));
                }
                if (results?.topics) {
                    actions.push(TopicRecordActions.updateTopics({ records: [...(results.topics?.values() || [])] }));
                }
                if (results?.assignments) {
                    actions.push(AssignmentRecordActions.updateAssignments({ records: [...(results.assignments?.values() || [])] }));
                }
                return actions;
            }),
            catchError(() => of(CastActions.stopSideRecordLoading()))
        );
    }

    getPublicationList(publicationListGuid: string) {
        if (!publicationListGuid) {
            const vs = this.store.selectSignal(selectCurrentCastViewState)() as PublicationViewStateRecord;
            const sections = vs.publicationChannel?.PSTPublicationChannelSections?.split('|')
                .map((item: string) => item.trim())
                .filter(Boolean);
            return from(this.castService.createPublicationList(vs, sections, {})).pipe(
                filter(res => res.status),
                map(res => res.data),
                catchError(() => of(null))
            );
        } else {
            return of(this.store.selectSignal(selectPublicationListsMap)().get(publicationListGuid));
        }
    }

    updatePublicationRecords(
        event: { sourceSection: string; targetSection: string; sourceIndex: number; targetIndex: number },
        newSections: StorytellingPublicationListSections,
        publicationList: Pick<StorytellingPublicationListRecord, 'Path' | 'Guid'>
    ) {
        const updateSequence$ = this.updatePublicationListSequence(newSections[SECTIONS_SEQUENCE_FIELD] || [], newSections, publicationList);
        const updateRecords$ = from(this.castService.updatePublicationRecords(event, newSections, publicationList));

        return forkJoin([updateSequence$, updateRecords$]).pipe(
            map(() => true),
            catchError(() => of(false))
        );
    }

    updatePublicationListSequence(
        sectionsSequence: Array<string>,
        publicationSections: StorytellingPublicationListSections,
        publicationList: Pick<StorytellingPublicationListRecord, 'Path' | 'Guid'>
    ) {
        return from(this.castService.updatePublicationListSequence(sectionsSequence, publicationSections, publicationList));
    }

    deletePublications(publications: Array<string>, publicationSections: StorytellingPublicationListSections, publicationList: Pick<StorytellingPublicationListRecord, 'Database' | 'Path' | 'Guid'>) {
        if (!publicationSections || !publicationList) return of(false);

        if (!publications?.length) return of(true);

        const deletePublicationsArgsList = publications.map(publication => {
            const { section, index } = [...(Object.keys(publicationSections) || [])].reduce(
                (acc, key) => {
                    if (key !== SECTIONS_SEQUENCE_FIELD && acc.index === -1) {
                        const localIndex = publicationSections[key].findIndex(p => p === publication);

                        return { section: key, index: localIndex };
                    }
                    return acc;
                },
                { section: '', index: -1 }
            );
            const publicationArgs: { publicationList: StorytellingPublicationListRecord; section: string; index: number; channelSections: StorytellingPublicationListSections } = {
                publicationList: publicationList,
                section,
                index,
                channelSections: publicationSections,
            };
            return publicationArgs;
        });
        return from(this.castService.deletePublicationRecords(deletePublicationsArgsList)).pipe(
            map(() => true),
            catchError(() => of(false))
        );
    }
}
